import React from "react";
import { useSelector } from "react-redux";
import { CalendarDays, ChevronRight } from "lucide-react";

const TableRow = ({ event, isHighlighted }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatEventType = (eventType) => {
    switch(eventType) {
      case 'VIRTUAL': return 'Virtual';
      case 'ON_CAMPUS': return 'Physical';
      default: return eventType || 'N/A';
    }
  };

  return (
    <div className="flex items-center h-11 w-full border-b-[1px] border-solid border-x-0 border-t-0 border-[#F1F5F9] last:border-b-0 text-[#334155] hover:bg-[#F8FAFC] transition">
      <div className="flex items-center px-1.5 py-0 w-[92px] flex-shrink-0">
        <div className="text-[#334155] text-[12px] font-poppins font-normal whitespace-nowrap">
          {formatDate(event.startTime)}
        </div>
      </div>
      <div className="flex items-center justify-center px-1.5 py-0 flex-1 min-w-0">
        <div
          className="text-[#334155] text-[12px] font-poppins font-normal truncate max-w-full text-center"
          title={event.title || 'N/A'}
        >
          {event.title || 'N/A'}
        </div>
      </div>
      <div className="flex items-center px-1.5 py-0 w-[64px] flex-shrink-0">
        <div className="text-[#334155] text-[12px] font-poppins font-normal whitespace-nowrap">
          {formatEventType(event.eventType)}
        </div>
      </div>
      <div className="flex items-center justify-center px-0 py-0 w-[30px] flex-shrink-0">
        <div className="flex items-center justify-center w-[26px] h-[26px] rounded-full bg-[#F1F5F9]">
          <ChevronRight size={14} className="text-[#64748B]" />
        </div>
      </div>
    </div>
  );
};

export default function EventParticipationTable() {
  // Data is fetched once by the Dashboard (useDashboardData)
  const { data: wellnessData, loading } = useSelector((state) => state.GET_WELLNESS);

  const handleViewAll = () => {
    window.location.href = "/calender";
  };

  // Extract events from API response
  const events = wellnessData?.message?.data?.payload?.data || [];
  const displayEvents = events.slice(0, 2); // Show only first 2 events

  return (
    <div className="p-[16px] w-full h-full bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm">
      <div className="flex flex-col gap-3 w-full">
        {/* Header */}
        <div className="flex justify-between items-center gap-2 -mx-[16px] -mt-[16px] px-[20px] py-[14px] bg-[#E7EDF5] rounded-t-[15px]">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex items-center justify-center w-[36px] h-[36px] rounded-xl bg-white text-[#EA580C] flex-shrink-0">
              <CalendarDays size={18} />
            </div>
            <h2 className="text-base font-semibold text-[#171717] font-poppins mb-0">
              Event Participation Stats
            </h2>
          </div>
          <button
            type="button"
            className="flex items-center gap-1 flex-shrink-0 whitespace-nowrap text-[#2563EB] font-poppins text-xs font-semibold bg-white hover:bg-[#F8FAFC] rounded-full px-3 py-1.5 border-0 transition-colors"
            onClick={handleViewAll}
          >
            View all
            <ChevronRight size={14} />
          </button>
        </div>

        {/* Table */}
        <div className="flex flex-col w-full min-w-0">
          {/* Header Row */}
          <div className="flex items-center rounded-xl overflow-hidden w-full bg-[#F8FAFC]">
            <div className="flex items-center px-1.5 py-2 w-[92px] flex-shrink-0">
              <div className="text-[#64748B] text-[11px] font-poppins font-semibold tracking-wide uppercase whitespace-nowrap">
                Date
              </div>
            </div>
            <div className="flex items-center justify-center px-1.5 py-2 flex-1 min-w-0">
              <div className="text-[#64748B] text-[11px] font-poppins font-semibold tracking-wide uppercase truncate max-w-full text-center">
                Title of Event
              </div>
            </div>
            <div className="flex items-center px-1.5 py-2 w-[64px] flex-shrink-0">
              <div className="text-[#64748B] text-[11px] font-poppins font-semibold tracking-wide uppercase whitespace-nowrap">
                Mode
              </div>
            </div>
            <div className="w-[30px] flex-shrink-0"></div>
          </div>

          {/* Data Rows */}
          {loading ? (
            <div className="text-center text-[#64748B] text-sm font-poppins py-4">Loading...</div>
          ) : displayEvents.length > 0 ? (
            displayEvents.map((event, index) => (
              <TableRow key={event.wellnessId} event={event} isHighlighted={index === 1 || index === 3} />
            ))
          ) : (
            <div className="text-center text-[#64748B] text-sm font-poppins py-4">No events found</div>
          )}
        </div>
      </div>
    </div>
  );
}